import { useEffect, useState } from 'react';
import { X, Save } from 'lucide-react';
import Button from '../../ui/Button';
import { Card, CardContent } from '../../ui/Card';
import irrigationNetworkService, {
  type IrrigationPipe,
  type PipeMaterial,
} from '../../../services/irrigationNetwork';
import { errorMessage } from '../../../utils/apiResponse';

interface Props {
  pipe: IrrigationPipe;
  materials: PipeMaterial[];
  editable: boolean;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
  onDeleted: () => void | Promise<void>;
}

export default function PipeEditDrawer({ pipe, materials, editable, onClose, onSaved }: Props) {
  const [draft, setDraft] = useState<IrrigationPipe>(pipe);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { setDraft(pipe); setError(null); }, [pipe]);

  const set = <K extends keyof IrrigationPipe>(k: K, v: IrrigationPipe[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await irrigationNetworkService.updatePipe(pipe.id, {
        material_key: draft.material_key,
        nominal_diameter_mm: draft.nominal_diameter_mm,
        internal_diameter_mm: draft.internal_diameter_mm,
        pressure_class_kpa: draft.pressure_class_kpa,
        roughness_override: draft.roughness_override,
        installed_year: draft.installed_year,
        notes: draft.notes,
      });
      await onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Save failed'));
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <CardContent>
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-sm">Pipe #{pipe.id}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-2 text-sm">
          <div className="text-xs text-gray-600">
            {pipe.from_node_id} → {pipe.to_node_id} · {pipe.length_m}m
          </div>

          <Field label="Material">
            <select className="input" disabled={!editable}
              value={draft.material_key}
              onChange={(e) => set('material_key', e.target.value)}>
              {materials.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.display_name} (C={m.hazen_williams_c})
                </option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Nominal Ø (mm)">
              <input className="input" type="number" disabled={!editable}
                value={draft.nominal_diameter_mm}
                onChange={(e) => set('nominal_diameter_mm', Number(e.target.value))} />
            </Field>
            <Field label="Internal Ø (mm)">
              <input className="input" type="number" step="0.1" disabled={!editable}
                value={draft.internal_diameter_mm}
                onChange={(e) => set('internal_diameter_mm', Number(e.target.value))} />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Field label="Pressure class (kPa)">
              <input className="input" type="number" disabled={!editable}
                value={draft.pressure_class_kpa ?? ''}
                onChange={(e) => set('pressure_class_kpa', e.target.value === '' ? null : Number(e.target.value))} />
            </Field>
            <Field label="Roughness override (C)">
              <input className="input" type="number" step="0.1" disabled={!editable}
                value={draft.roughness_override ?? ''}
                onChange={(e) => set('roughness_override', e.target.value === '' ? null : Number(e.target.value))} />
            </Field>
          </div>

          <Field label="Installed year">
            <input className="input" type="number" disabled={!editable}
              value={draft.installed_year ?? ''}
              onChange={(e) => set('installed_year', e.target.value === '' ? null : Number(e.target.value))} />
          </Field>

          <Field label="Notes">
            <textarea className="input" rows={2} disabled={!editable}
              value={draft.notes ?? ''}
              onChange={(e) => set('notes', e.target.value)} />
          </Field>

          {error && <div className="text-red-600 text-xs">{error}</div>}

          {editable && (
            <Button size="sm" onClick={save} disabled={saving} className="w-full">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          )}
        </div>
      </CardContent>
      <style>{`.input{width:100%;border:1px solid #d1d5db;border-radius:6px;padding:4px 8px;font-size:0.875rem;background:#fff}`}</style>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-600 mb-0.5">{label}</div>
      {children}
    </div>
  );
}
